import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Panel, Loading, Field, NativeSelect } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Breaks() {
  const [r, setR] = useState(null);
  const [biz, setBiz] = useState("");
  const [clients, setClients] = useState([]);
  const { user } = useAuth();
  const canEdit = user.role === "superadmin";

  useEffect(() => {
    api.get("/admin/clients").then((x) => setClients(x.data));
  }, []);

  useEffect(() => {
    setR(null);
    api.get("/admin/programming/rules", { params: { business_id: biz } }).then((x) => setR(x.data));
  }, [biz]);

  if (!r) return <Loading />;

  const save = async () => {
    try {
      await api.put("/admin/programming/rules", {
        ads_paused: Boolean(r.ads_paused),
        enabled: Boolean(r.enabled),
      }, { params: { business_id: biz } });
      toast.success("Configuração salva. A sequência fixa vale a partir da próxima transição.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Intervalos comerciais"
        subtitle="A programação é fixa: uma música e, em seguida, um anúncio. Não há limite de repetições por hora."
      >
        {canEdit && <Button data-testid="breaks-save-btn" className="rounded-full" onClick={save}>Salvar configuração</Button>}
      </PageHeader>

      <Panel>
        <Field label="Escopo da configuração" hint="O controle pode ser aplicado globalmente ou individualmente a um cliente.">
          <NativeSelect testid="breaks-scope-select" value={biz} onChange={setBiz}>
            <option value="">Global (todas as rádios)</option>
            {clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
          </NativeSelect>
        </Field>
        {biz && (
          <label className="flex items-center gap-3 text-sm mt-4">
            <Switch
              data-testid="breaks-override-switch"
              checked={!!r.enabled}
              onCheckedChange={(value) => setR({ ...r, enabled: value })}
            />
            Usar configuração própria para este cliente
          </label>
        )}
      </Panel>

      <Panel title="Sequência automática da rádio">
        <div className="rounded-xl border border-border bg-secondary/30 p-5 space-y-2">
          <p className="text-lg font-semibold">1 música + 1 anúncio</p>
          <p className="text-sm text-slate-300 leading-relaxed">
            A cada música, a rádio tenta reproduzir exatamente um anúncio antes da próxima música.
            O anúncio entra nos segundos finais da música, que continua tocando em volume baixo.
            Quando o anúncio termina, a música restante é encerrada junto e a próxima faixa começa.
            Quando houver vários anúncios válidos, eles serão alternados em fila, começando pelo mais
            recentemente publicado. Ao chegar ao fim da fila, a sequência recomeça.
          </p>
          <p className="text-sm text-slate-400 leading-relaxed">
            Mesmo que exista apenas um anúncio em veiculação, ele poderá tocar novamente após cada música.
            A validade, os dias e os horários definidos no cadastro de cada anúncio continuam sendo respeitados.
          </p>
        </div>
      </Panel>

      <Panel title="Controle de veiculação">
        <label className="flex items-center gap-3 text-sm">
          <Switch
            data-testid="breaks-paused-switch"
            checked={!!r.ads_paused}
            onCheckedChange={(value) => setR({ ...r, ads_paused: value })}
          />
          Pausar temporariamente todos os anúncios
        </label>
        <p className="text-xs text-slate-500 mt-3">
          Quando a pausa estiver ativa, a rádio continuará tocando músicas. O ciclo de anúncios será retomado
          automaticamente quando a pausa for desativada.
        </p>
      </Panel>

      {!canEdit && <p className="text-sm text-slate-500">Somente o superadministrador pode alterar esta configuração.</p>}
    </div>
  );
}
